/*
 * Engraving Studio — live caseback engraving configurator for Dawn 15.
 *
 * Adds the watch with engraving line-item properties, then a linked engraving
 * fee line (same _engraving_id). The main "Add to cart" button is routed
 * through here whenever an engraving is switched on.
 */
(() => {
  if (customElements.get('engraving-studio')) return;

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const ART_R = 212; // radius of the engraving disc on the drawn caseback
  const MAX_FILE_MB = 10;

  const FONTS = {
    script: { label: 'Script', family: "'Pinyon Script', cursive", style: 'normal', scale: 1.2, upper: false, track: 0 },
    roman: { label: 'Roman', family: "'Cormorant Garamond', serif", style: 'italic', scale: 1, upper: false, track: 0.02 },
    capitals: { label: 'Capitals', family: "'Cinzel', serif", style: 'normal', scale: 0.8, upper: true, track: 0.08 },
    modern: { label: 'Modern', family: "'Montserrat', sans-serif", style: 'normal', scale: 0.7, upper: true, track: 0.2 },
  };

  const MESSAGES = {
    initials: 'Add your initials to continue.',
    message: 'Add a message to continue.',
    logo: 'Upload your design to continue.',
    consent: 'Please confirm the personalisation terms.',
    unavailable: 'Engraving is not available for this piece right now.',
    soldOut: 'This variant is sold out.',
    file: `Please upload a PNG, JPG or WEBP under ${MAX_FILE_MB}MB.`,
    generic: 'Something went wrong. Please try again.',
  };

  const svgEl = (tag, attrs = {}, text) => {
    const node = document.createElementNS(SVG_NS, tag);
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    if (text != null) node.textContent = text;
    return node;
  };

  // The section renders from the header group; place it right after the main product section.
  // Returns true when it moved, in which case the element reconnects at its new position.
  const relocate = (studio) => {
    const wrapper = studio.closest('.eng');
    const section = wrapper.closest('.shopify-section') || wrapper;
    const main = document.querySelector('[id^="MainProduct-"]');
    const host = main && main.closest('.shopify-section');
    wrapper.style.removeProperty('display');
    if (!host || host.nextElementSibling === section) return false;
    host.after(section);
    return true;
  };

  class EngravingStudio extends HTMLElement {
    connectedCallback() {
      if (relocate(this)) return;
      this.cfg = JSON.parse(this.querySelector('[data-eng-config]').textContent);
      this.uid = `eng-${this.cfg.sectionId}`.replace(/[^\w-]/g, '');
      this.state = {
        mode: 'initials',
        enabled: false,
        consent: false,
        initials: '',
        line: '',
        arc: '',
        arcPos: 'top',
        font: 'script',
        size: 100,
        notes: '',
        metal: this.cfg.metal || 'steel',
        file: null,
        fileUrl: null,
        variantId: String(this.cfg.variantId),
      };

      this.stage = this.querySelector('[data-eng-stage]');
      this.buildPreview();

      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.setupDrop();

      this.onMainSubmit = this.onMainSubmit.bind(this);
      document.addEventListener('submit', this.onMainSubmit, true);
      this.onDocChange = (event) => {
        if (event.target.closest && event.target.closest('variant-selects, [name="id"]')) {
          setTimeout(() => this.syncVariant(), 400);
        }
      };
      document.addEventListener('change', this.onDocChange);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event && event.data && event.data.variant;
          if (variant) this.syncVariant(String(variant.id), variant);
        });
      }

      this.syncVariant();
      if (document.fonts) document.fonts.ready.then(() => this.render());
    }

    disconnectedCallback() {
      if (!this.cfg) return;
      document.removeEventListener('submit', this.onMainSubmit, true);
      document.removeEventListener('change', this.onDocChange);
      if (this.unsubscribe) this.unsubscribe();
      if (this.state.fileUrl) URL.revokeObjectURL(this.state.fileUrl);
    }

    /* ---------- Preview ---------- */

    buildPreview() {
      const id = this.uid;
      const stop = (offset, color) => `<stop offset="${offset}" style="stop-color:var(${color})"/>`;
      const luminanceInvert = '-0.2126 -0.7152 -0.0722 0 1';
      this.querySelector('[data-eng-canvas]').innerHTML = `
        <svg viewBox="0 0 600 600" xmlns="${SVG_NS}" role="img" aria-label="Live caseback engraving preview">
          <defs>
            <radialGradient id="${id}-case" cx="36%" cy="30%" r="80%">${stop(0, '--m-hi')}${stop(0.45, '--m-mid')}${stop(1, '--m-lo')}</radialGradient>
            <linearGradient id="${id}-knurl" x1="0" y1="0" x2="1" y2="1">${stop(0, '--m-lo')}${stop(0.35, '--m-hi')}${stop(0.7, '--m-lo')}${stop(1, '--m-mid')}</linearGradient>
            <radialGradient id="${id}-disc" cx="34%" cy="26%" r="90%">${stop(0, '--m-hi')}${stop(0.5, '--m-mid')}${stop(1, '--m-lo')}</radialGradient>
            <linearGradient id="${id}-sheen" x1="0" y1="0" x2="1" y2="0.3">
              <stop offset="0" stop-color="#fff" stop-opacity="0"/>
              <stop offset="0.5" stop-color="#fff" stop-opacity="0.6"/>
              <stop offset="1" stop-color="#fff" stop-opacity="0"/>
            </linearGradient>
            <filter id="${id}-brush" x="0" y="0" width="100%" height="100%">
              <feTurbulence type="fractalNoise" baseFrequency="0.9 0.012" numOctaves="2" seed="4"/>
              <feColorMatrix values="0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 0.09 0"/>
              <feComposite in2="SourceGraphic" operator="in"/>
            </filter>
            <filter id="${id}-ink" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
              <feColorMatrix values="${luminanceInvert}  ${luminanceInvert}  ${luminanceInvert}  0 0 0 1 0"/>
              <feComponentTransfer>
                <feFuncR type="linear" slope="1.8" intercept="-0.35"/>
                <feFuncG type="linear" slope="1.8" intercept="-0.35"/>
                <feFuncB type="linear" slope="1.8" intercept="-0.35"/>
              </feComponentTransfer>
            </filter>
            <mask id="${id}-logo" maskUnits="userSpaceOnUse" x="0" y="0" width="600" height="600">
              <image data-eng-mask-image filter="url(#${id}-ink)" preserveAspectRatio="xMidYMid meet"/>
            </mask>
            <clipPath id="${id}-clip"><circle data-eng-clip cx="300" cy="300" r="${ART_R}"/></clipPath>
            <path id="${id}-arc-top" data-eng-arc="top" fill="none"/>
            <path id="${id}-arc-bottom" data-eng-arc="bottom" fill="none"/>
          </defs>

          <g class="eng-art">
            <circle cx="300" cy="300" r="294" fill="url(#${id}-case)"/>
            <circle cx="300" cy="300" r="294" fill="none" stroke="rgba(0,0,0,.22)" stroke-width="1.5"/>
            <circle cx="300" cy="300" r="270" fill="none" stroke="url(#${id}-knurl)" stroke-width="26" stroke-dasharray="1.6 2.6"/>
            <circle cx="300" cy="300" r="283" fill="none" stroke="rgba(0,0,0,.18)" stroke-width="1"/>
            <circle cx="300" cy="300" r="257" fill="url(#${id}-case)"/>
            <circle cx="300" cy="300" r="257" fill="none" stroke="rgba(0,0,0,.25)" stroke-width="1.2"/>
            <circle cx="300" cy="300" r="255" fill="none" stroke="rgba(255,255,255,.7)" stroke-width="1"/>
            <circle cx="300" cy="300" r="238" fill="none" stroke="rgba(0,0,0,.12)" stroke-width="1"/>
            <circle cx="300" cy="300" r="${ART_R + 4}" fill="url(#${id}-disc)"/>
            <circle cx="300" cy="300" r="${ART_R + 4}" fill="#fff" filter="url(#${id}-brush)"/>
            <circle cx="300" cy="300" r="${ART_R + 4}" fill="none" stroke="rgba(0,0,0,.28)" stroke-width="1.4"/>
            <circle cx="300" cy="300" r="${ART_R + 2}" fill="none" stroke="rgba(255,255,255,.75)" stroke-width="1"/>
          </g>
          <image class="eng-photo" data-eng-photo x="0" y="0" width="600" height="600" preserveAspectRatio="xMidYMid slice"/>

          <g class="eng-cut" data-eng-cut clip-path="url(#${id}-clip)">
            <use href="#${id}-shape" style="fill:var(--cut-hi)" transform="translate(0.9 1.3)"/>
            <g style="fill:var(--cut)"><g id="${id}-shape" data-eng-shape></g></g>
          </g>
          <rect class="eng-sheen" x="0" y="0" width="260" height="600" fill="url(#${id}-sheen)" clip-path="url(#${id}-clip)"/>
        </svg>`;

      this.svg = this.querySelector('[data-eng-canvas] svg');
      this.shape = this.svg.querySelector('[data-eng-shape]');
      this.cut = this.svg.querySelector('[data-eng-cut]');
      this.photo = this.svg.querySelector('[data-eng-photo]');
      this.maskImage = this.svg.querySelector('[data-eng-mask-image]');
      this.setArea({ cx: 300, cy: 300, r: ART_R });
    }

    setArea(area) {
      this.area = area;
      const { cx, cy, r } = area;
      const clip = this.svg.querySelector('[data-eng-clip]');
      clip.setAttribute('cx', cx);
      clip.setAttribute('cy', cy);
      clip.setAttribute('r', r);
      // Top arc runs clockwise (letters face out), bottom arc counter-clockwise (letters stay upright).
      const arc = (radius, sweep) => `M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 ${sweep} ${cx + radius} ${cy}`;
      this.svg.querySelector('[data-eng-arc="top"]').setAttribute('d', arc(r * 0.74, 1));
      this.svg.querySelector('[data-eng-arc="bottom"]').setAttribute('d', arc(r * 0.8, 0));
    }

    setPhoto(url) {
      this.stage.classList.toggle('has-photo', Boolean(url));
      if (url) {
        this.photo.setAttribute('href', url);
        const [x, y, r] = String(this.cfg.area || '50,50,30').split(',').map((n) => parseFloat(n) * 6);
        this.setArea({ cx: x || 300, cy: y || 300, r: r || 180 });
      } else {
        this.photo.removeAttribute('href');
        this.setArea({ cx: 300, cy: 300, r: ART_R });
      }
    }

    drawEngraving() {
      const s = this.state;
      const font = FONTS[s.font];
      const { cx, cy, r } = this.area;
      const k = (r / ART_R) * (s.size / 100);
      const ghost = !this.hasContent();
      const sample = this.cfg.sample || {};

      this.shape.replaceChildren();
      this.cut.style.opacity = ghost ? 0.3 : 1;

      const addText = (value, size, maxWidth, path) => {
        const shown = font.upper ? value.toUpperCase() : value;
        const text = svgEl('text', {
          'font-family': font.family,
          'font-style': font.style,
          'font-size': size.toFixed(1),
          'letter-spacing': (size * (path ? Math.max(font.track, 0.06) : font.track)).toFixed(2),
          'text-anchor': 'middle',
        });
        if (path) {
          text.append(svgEl('textPath', { href: `#${this.uid}-arc-${path}`, startOffset: '50%' }, shown));
        } else {
          text.setAttribute('x', cx);
          text.setAttribute('y', cy);
          text.setAttribute('dominant-baseline', 'central');
          text.textContent = shown;
        }
        this.shape.append(text);
        const width = text.getComputedTextLength();
        if (width > maxWidth) text.setAttribute('font-size', ((size * maxWidth) / width).toFixed(1));
        return text;
      };

      if (s.mode === 'initials') {
        const value = ghost ? sample.initials || 'S.F' : s.initials.trim();
        const base = value.length <= 2 ? 150 : value.length === 3 ? 124 : 98;
        addText(value, base * font.scale * k, r * 1.5);
      } else if (s.mode === 'message') {
        const line = ghost ? sample.line || '24 · 10 · 2026' : s.line.trim();
        const arc = ghost ? sample.arc || 'Forever yours' : s.arc.trim();
        if (line) addText(line, 54 * font.scale * k, r * 1.35);
        if (arc) {
          const arcRadius = r * (s.arcPos === 'top' ? 0.74 : 0.8);
          addText(arc, 30 * Math.min(font.scale, 1.1) * k, Math.PI * arcRadius * 0.82, s.arcPos);
        }
      } else if (s.fileUrl && !ghost) {
        const size = r * 1.3 * (s.size / 100);
        Object.entries({ href: s.fileUrl, x: cx - size / 2, y: cy - size / 2, width: size, height: size }).forEach(
          ([key, value]) => this.maskImage.setAttribute(key, value)
        );
        this.shape.append(
          svgEl('rect', { x: cx - r, y: cy - r, width: r * 2, height: r * 2, mask: `url(#${this.uid}-logo)` })
        );
      } else {
        const ring = svgEl('circle', {
          cx,
          cy,
          r: r * 0.46,
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': 1.5,
          'stroke-dasharray': '6 7',
        });
        ring.style.stroke = 'var(--cut)';
        this.shape.append(ring);
        addText('Your design', 22 * k, r, null).setAttribute('font-family', FONTS.capitals.family);
      }
    }

    polish() {
      clearTimeout(this.polishTimer);
      this.polishTimer = setTimeout(() => {
        this.stage.classList.remove('is-polishing');
        void this.stage.offsetWidth;
        this.stage.classList.add('is-polishing');
      }, 220);
    }

    /* ---------- State ---------- */

    limit(key) {
      return { initials: this.cfg.maxInitials, line: this.cfg.maxLine, arc: this.cfg.maxArc, notes: 300 }[key];
    }

    clean(key, value) {
      const patterns = {
        initials: /[^\p{L}\p{N}.&\- ]/gu,
        line: /[^\p{L}\p{N} .,'’&\-!?:\/+·♥]/gu,
        arc: /[^\p{L}\p{N} .,'’&\-!?:\/+·♥]/gu,
      };
      const cleaned = patterns[key] ? value.replace(patterns[key], '') : value;
      return cleaned.slice(0, this.limit(key) || 300);
    }

    hasContent() {
      const s = this.state;
      if (s.mode === 'initials') return Boolean(s.initials.trim());
      if (s.mode === 'message') return Boolean(s.line.trim() || s.arc.trim());
      return Boolean(s.file);
    }

    isActive() {
      return this.state.enabled && this.hasContent();
    }

    summaryText() {
      const s = this.state;
      const style = FONTS[s.font].label;
      if (s.mode === 'initials') return s.initials.trim() ? `“${s.initials.trim()}” · ${style}` : '';
      if (s.mode === 'message') {
        const parts = [s.line.trim(), s.arc.trim()].filter(Boolean).map((part) => `“${part}”`);
        return parts.length ? `${parts.join(' + ')} · ${style}` : '';
      }
      return s.file ? `Your design · ${s.file.name}` : '';
    }

    properties() {
      const s = this.state;
      const props = { 'Engraving type': { initials: 'Initials', message: 'Message', logo: 'Custom design' }[s.mode] };
      if (s.mode === 'initials') props['Engraving'] = s.initials.trim();
      if (s.mode === 'message') {
        if (s.line.trim()) props['Engraving (centre)'] = s.line.trim();
        if (s.arc.trim()) props[`Engraving (curved ${s.arcPos})`] = s.arc.trim();
      }
      if (s.mode !== 'logo') props['Engraving style'] = FONTS[s.font].label;
      props['Engraving size'] = `${s.size}%`;
      if (s.mode === 'logo' && s.notes.trim()) props['Engraving notes'] = s.notes.trim();
      return props;
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(this.cfg.locale, {
          style: 'currency',
          currency: this.cfg.currency,
          minimumFractionDigits: cents % 100 ? 2 : 0,
        }).format(cents / 100);
      } catch (error) {
        return (cents / 100).toFixed(2);
      }
    }

    /* ---------- Rendering ---------- */

    render() {
      const s = this.state;
      const font = FONTS[s.font];
      const active = this.isActive();

      this.querySelectorAll('[data-eng-tab]').forEach((tab) => {
        const selected = tab.dataset.engTab === s.mode;
        tab.setAttribute('aria-selected', selected);
        tab.tabIndex = selected ? 0 : -1;
      });
      this.querySelectorAll('[data-eng-panel]').forEach((panel) => (panel.hidden = panel.dataset.engPanel !== s.mode));
      this.querySelectorAll('[data-eng-font]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.engFont === s.font));
      this.querySelectorAll('[data-eng-arcpos]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.engArcpos === s.arcPos));
      this.querySelectorAll('[data-eng-metal]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.engMetal === s.metal));
      this.querySelector('[data-eng-styles]').hidden = s.mode === 'logo';

      this.querySelectorAll('[data-eng-typed]').forEach((input) => {
        input.style.fontFamily = font.family;
        input.style.fontStyle = font.style;
        input.style.textTransform = font.upper ? 'uppercase' : 'none';
        input.style.letterSpacing = font.upper ? `${font.track}em` : '';
      });
      this.querySelectorAll('[data-eng-count]').forEach((counter) => {
        const key = counter.dataset.engCount;
        counter.textContent = `${s[key].length} / ${this.limit(key)}`;
      });

      const fileBox = this.querySelector('[data-eng-file]');
      const drop = this.querySelector('[data-eng-drop]');
      if (fileBox && drop) {
        fileBox.hidden = !s.file;
        drop.hidden = Boolean(s.file);
        if (s.file) {
          fileBox.querySelector('img').src = s.fileUrl;
          fileBox.querySelector('[data-eng-file-name]').textContent = s.file.name;
        }
      }

      this.stage.dataset.metal = s.metal;
      this.classList.toggle('is-active', active);
      this.querySelector('[data-eng-field="enabled"]').checked = s.enabled;
      this.querySelector('[data-eng-field="consent"]').checked = s.consent;

      this.drawEngraving();
      this.renderSummary(active);
    }

    renderSummary(active) {
      const variant = this.variant || {};
      const watch = variant.price != null ? variant.price : this.cfg.price;
      const fee = this.cfg.feePrice || 0;
      const summary = this.summaryText();

      this.querySelector('[data-eng-fee]').textContent = `+ ${this.money(fee)}`;
      this.querySelector('[data-eng-detail]').textContent = summary || this.cfg.strings.emptySummary;

      const cta = this.querySelector('[data-eng-submit]');
      const label = cta.querySelector('[data-eng-cta-label]');
      const available = variant.available !== false;
      cta.disabled = !available || !this.cfg.feeVariantId;
      if (!this.busy) {
        label.textContent = available
          ? `${this.cfg.strings.cta} — ${this.money(watch + fee)}`
          : this.cfg.strings.soldOut;
      }

      document.querySelectorAll('[data-eng-trigger]').forEach((trigger) => {
        trigger.classList.toggle('is-added', active);
        const state = trigger.querySelector('[data-eng-trigger-state]');
        const detail = trigger.querySelector('[data-eng-trigger-detail]');
        if (state) state.textContent = active ? 'Added ✓' : 'Personalise';
        if (detail && active) detail.textContent = summary;
        else if (detail) detail.textContent = detail.dataset.default || '';
      });
    }

    syncVariant(id, variant) {
      const input = this.mainForm() && this.mainForm().querySelector('[name="id"]');
      this.state.variantId = String(id || (input && input.value) || this.state.variantId);
      this.variant = this.cfg.variants[this.state.variantId] ||
        (variant ? { price: variant.price, available: variant.available } : {});
      this.setPhoto(this.variant.caseback || this.cfg.caseback);
      if (this.variant.metal) this.state.metal = this.variant.metal;
      this.ensureTrigger();
      this.render();
    }

    // Puts the "Personalise your caseback" row just above the buy buttons, unless the
    // product info already has one (e.g. from a Custom liquid block).
    ensureTrigger() {
      const form = this.mainForm();
      const productForm = form && form.closest('product-form');
      const info = form && form.closest('[id^="MainProduct-"]');
      const template = this.querySelector('template[data-eng-trigger-template]');
      if (!productForm || !template || (info && info.querySelector('[data-eng-trigger]'))) return;
      productForm.before(template.content.cloneNode(true));
    }

    /* ---------- Events ---------- */

    onInput(event) {
      const key = event.target.dataset.engField;
      if (!key || event.target.type === 'checkbox' || event.target.type === 'file') return;
      if (event.target.type === 'range') {
        this.state[key] = Number(event.target.value);
      } else {
        const value = this.clean(key, event.target.value);
        if (value !== event.target.value) event.target.value = value;
        const hadContent = this.hasContent();
        this.state[key] = value;
        if (!hadContent && this.hasContent()) this.state.enabled = true;
        this.polish();
      }
      this.clearMessages();
      this.render();
    }

    onChange(event) {
      const target = event.target;
      if (target.type === 'checkbox' && target.dataset.engField) {
        this.state[target.dataset.engField] = target.checked;
        this.clearMessages();
        this.render();
      } else if (target.type === 'file') {
        this.setFile(target.files && target.files[0]);
        target.value = '';
      }
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      const data = button.dataset;
      if (data.engTab) {
        this.state.mode = data.engTab;
        if (this.hasContent()) this.state.enabled = true;
        this.polish();
      } else if (data.engFont) {
        this.state.font = data.engFont;
        this.polish();
      } else if (data.engArcpos) {
        this.state.arcPos = data.engArcpos;
      } else if (data.engMetal) {
        this.state.metal = data.engMetal;
        this.polish();
      } else if ('engClearFile' in data) {
        this.setFile(null);
        return;
      } else if ('engSubmit' in data) {
        event.preventDefault();
        this.state.enabled = true;
        this.addToCart(button);
        return;
      } else {
        return;
      }
      this.clearMessages();
      this.render();
    }

    onKeydown(event) {
      const tab = event.target.closest('[data-eng-tab]');
      if (!tab || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      const tabs = [...this.querySelectorAll('[data-eng-tab]')];
      const next = tabs[(tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
      next.focus();
      next.click();
    }

    setupDrop() {
      const drop = this.querySelector('[data-eng-drop]');
      if (!drop) return;
      ['dragenter', 'dragover'].forEach((type) =>
        drop.addEventListener(type, (event) => {
          event.preventDefault();
          drop.classList.add('is-over');
        })
      );
      ['dragleave', 'drop'].forEach((type) => drop.addEventListener(type, () => drop.classList.remove('is-over')));
      drop.addEventListener('drop', (event) => {
        event.preventDefault();
        this.setFile(event.dataTransfer.files[0]);
      });
    }

    setFile(file) {
      if (file && (!/^image\/(png|jpe?g|webp)$/.test(file.type) || file.size > MAX_FILE_MB * 1024 * 1024)) {
        this.showError(MESSAGES.file);
        return;
      }
      if (this.state.fileUrl) URL.revokeObjectURL(this.state.fileUrl);
      this.state.file = file || null;
      this.state.fileUrl = file ? URL.createObjectURL(file) : null;
      if (file) {
        this.state.enabled = true;
        this.polish();
      }
      this.clearMessages();
      this.render();
    }

    /* ---------- Cart ---------- */

    mainForm() {
      return (
        document.querySelector('[id^="MainProduct-"] product-form form[action*="/cart/add"]') ||
        document.querySelector('product-info product-form form[action*="/cart/add"]')
      );
    }

    onMainSubmit(event) {
      const form = this.mainForm();
      if (!form || event.target !== form || !this.state.enabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addToCart(form.querySelector('[type="submit"]'), true);
    }

    validate() {
      if (!this.cfg.feeVariantId) return MESSAGES.unavailable;
      if (this.variant && this.variant.available === false) return MESSAGES.soldOut;
      if (!this.hasContent()) return MESSAGES[this.state.mode];
      if (!this.state.consent) return MESSAGES.consent;
      return '';
    }

    async addToCart(button, fromMainForm = false) {
      if (this.busy) return;
      const error = this.validate();
      if (error) {
        this.showError(error);
        if (fromMainForm) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const form = this.mainForm();
      const variantId = (form && form.querySelector('[name="id"]').value) || this.state.variantId;
      // getAttribute: Dawn's <input name="id"> shadows form.id.
      const quantityInput = form && document.querySelector(`[name="quantity"][form="${form.getAttribute('id')}"]`);
      const quantity = Math.max(1, parseInt(quantityInput && quantityInput.value, 10) || 1);
      const engravingId = `ENG-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`.toUpperCase();
      const drawer = document.querySelector('cart-drawer');

      this.setBusy(true, button);
      let watchLine;
      try {
        const body = new FormData();
        body.append('id', variantId);
        body.append('quantity', quantity);
        Object.entries(this.properties()).forEach(([key, value]) => body.append(`properties[${key}]`, value));
        body.append('properties[_engraving_id]', engravingId);
        if (this.state.mode === 'logo' && this.state.file) body.append('properties[Engraving artwork]', this.state.file);
        watchLine = await this.post('cart/add.js', body);

        const cart = await this.post(
          'cart/add.js',
          JSON.stringify({
            items: [
              {
                id: this.cfg.feeVariantId,
                quantity,
                properties: { For: this.cfg.productTitle, _engraving_id: engravingId, _engraving_role: 'fee' },
              },
            ],
            sections: drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender().map((s) => s.id) : [],
            sections_url: window.location.pathname,
          })
        );
        this.onAdded(cart, drawer, variantId);
      } catch (err) {
        if (watchLine && watchLine.key) {
          this.post('cart/change.js', JSON.stringify({ id: watchLine.key, quantity: 0 })).catch(() => {});
        }
        this.showError((err && err.message) || MESSAGES.generic);
      } finally {
        this.setBusy(false, button);
      }
    }

    async post(path, body) {
      const isJson = typeof body === 'string';
      const response = await fetch(ROOT + path, {
        method: 'POST',
        headers: isJson ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || MESSAGES.generic);
      return data;
    }

    onAdded(cart, drawer, variantId) {
      this.querySelector('[data-eng-success]').textContent = this.cfg.strings.added;
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'engraving-studio', productVariantId: variantId, cartData: cart });
      }
      if (drawer && cart.sections && drawer.renderContents) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(cart);
      } else {
        window.location.href = (window.routes && window.routes.cart_url) || `${ROOT}cart`;
      }
    }

    setBusy(busy, button) {
      this.busy = busy;
      const cta = this.querySelector('[data-eng-submit]');
      [cta, button].filter(Boolean).forEach((b) => {
        b.setAttribute('aria-busy', busy);
        b.classList.toggle('loading', busy);
        const spinner = b.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !busy);
      });
      if (busy) cta.querySelector('[data-eng-cta-label]').textContent = this.cfg.strings.adding;
      else this.render();
    }

    showError(message) {
      this.querySelector('[data-eng-success]').textContent = '';
      this.querySelector('[data-eng-error]').textContent = message;
      this.classList.remove('is-shaking');
      void this.offsetWidth;
      this.classList.add('is-shaking');
    }

    clearMessages() {
      this.querySelector('[data-eng-error]').textContent = '';
      this.querySelector('[data-eng-success]').textContent = '';
    }
  }

  customElements.define('engraving-studio', EngravingStudio);
})();
